import type en from '../en/templates'
import type { Translation } from '../types'

/** Configuration templates: the list, the wizard and the blueprints it renders */
const templates: Translation<typeof en> = {
  "templates.title": "Plantillas",
  "templates.subtitle": "Configuración reutilizable para sus sitios",
  "templates.new": "Nueva plantilla",
  "templates.allTypes": "Todos los tipos",
  "templates.empty": "Todavía no hay plantillas",
  "templates.emptyText": "Cree una plantilla tomando la configuración de un sitio existente o constrúyala usted mismo.",
  "templates.createFirst": "Crear la primera plantilla",
  "templates.family.ANY": "Cualquier dispositivo",
  "templates.defaultBadge": "PREDETERMINADA",
  "templates.origin.PULLED": "importada",
  "templates.origin.MANUAL": "creada",
  "templates.system": "Sistema",
  "templates.applications": { one: "aplicada {count} vez", other: "aplicada {count} veces" },
  "templates.editHint": "Editar la plantilla y su configuración",
  "templates.apply": "Aplicar",
  "templates.deleteNamed": "Eliminar {name}",
  "templates.deleteConfirm": "¿Eliminar la plantilla «{name}»?",
  "templates.applyTitle": "Aplicar plantilla",
  "templates.queued": { one: "En cola para {count} sitio", other: "En cola para {count} sitios" },
  "templates.queuedText": "Cada sitio indica qué campos se aplicaron. Los campos no admitidos se registran, no se descartan en silencio.",
  "templates.done": "Listo",
  "templates.targets": "«{name}» es para dispositivos {family}.",
  "templates.noCompatible": "Ningún sitio conectado puede recibir esta plantilla. Compruebe para qué familia de dispositivos es.",
  "templates.partial": { one: "{count} sitio elegido omitirá algunos campos:", other: "{count} sitios elegidos omitirán algunos campos:" },
  "templates.more": "+{count} más",
  "templates.showIncompatible": { one: "Mostrar {count} sitio que no puede recibirla", other: "Mostrar {count} sitios que no pueden recibirla" },
  "templates.hideIncompatible": { one: "Ocultar {count} sitio que no puede recibirla", other: "Ocultar {count} sitios que no pueden recibirla" },
  "templates.notLicensed": "no incluido en su licencia",
  "templates.noSupported": "ningún campo admitido",
  "templates.wrongFamily": "otra familia de dispositivos",
  "templates.applyTo": { one: "Aplicar a {count} sitio", other: "Aplicar a {count} sitios" },
  "templates.allSupported": "Todos los campos admitidos",
  "templates.skipped": { one: "Se omitirá {count} campo", other: "Se omitirán {count} campos" },
  "templates.family.UNIFI": "Controladores y Site Manager",
  "templates.family.VELANOX": "WAN agregada One Speed",
  "templates.family.TELTONIKA": "Routers móviles RutOS",
  "templates.family.PEPLINK": "Balance / MAX con SpeedFusion",
  "templates.family.MIKROTIK": "Routers RouterOS y enlaces de radio PtP / PtMP",
  "templates.saveFailed": "No se pudo guardar la plantilla",
  "templates.pullFailed": "No se pudo importar la configuración",
  "templates.updated": "Plantilla actualizada",
  "templates.saved": "Plantilla guardada",
  "templates.viewAll": "Ver plantillas",
  "templates.createAnother": "Crear otra",
  "templates.back": "Atrás",
  "templates.editing": "Editar: {name}",
  "templates.whichKind": "¿Para qué tipo de dispositivo es?",
  "templates.sitesConnected": { one: "{count} sitio conectado", other: "{count} sitios conectados" },
  "templates.named": "Plantilla {name}",
  "templates.whatConfigure": "¿Qué desea configurar?",
  "templates.variant": "Variante de destino",
  "templates.variantHint": "Las variantes gestionadas desde la nube admiten menos cambios que el acceso directo al dispositivo.",
  "templates.loadingTypes": "Cargando tipos de plantilla…",
  "templates.howCreate": "¿Cómo desea crearla?",
  "templates.buildWizard": "Crear con el asistente",
  "templates.buildHint": "Paso a paso, mostrando solo lo que admiten los dispositivos {family}.",
  "templates.pullFromSite": "Importar de un sitio",
  "templates.noPullSites": "Todavía no hay sitios con controlador UniFi conectados.",
  "templates.pullOnlyFor": "Disponible para plantillas de red, wifi y WAN desde un sitio con controlador UniFi.",
  "templates.pullFrom": { one: "Copie la configuración actual de su sitio con controlador UniFi.", other: "Copie la configuración actual de uno de sus {count} sitios con controlador UniFi." },
  "templates.sourceSite": "Sitio de origen *",
  "templates.chooseSite": "Elija un sitio…",
  "templates.name": "Nombre de la plantilla *",
  "templates.description": "Descripción",
  "templates.optional": "Opcional",
  "templates.optionalParen": "(opcional)",
  "templates.pullConfig": "Importar configuración",
  "templates.coverage": "{provider} admite {count} de las {total} opciones de este tipo de plantilla.",
  "templates.hiddenSteps": { one: "{count} paso está oculto por completo.", other: "{count} pasos están ocultos por completo." },
  "templates.stepOf": "Paso {n} de {total}",
  "templates.nothingHere": "No hay nada que configurar en este paso para {provider}.",
  "templates.showUnsupported": { one: "Mostrar {count} opción no admitida", other: "Mostrar {count} opciones no admitidas" },
  "templates.hideUnsupported": { one: "Ocultar {count} opción no admitida", other: "Ocultar {count} opciones no admitidas" },
  "templates.saveTemplate": "Guardar plantilla",
  "templates.updateTemplate": "Actualizar plantilla",
  "templates.myName": "Mi {name}",
  "templates.useSuggestion": "Usar «{name}»",
  "templates.next": "Siguiente",
  "templates.notSupported": "no admitido",
  "templates.enabled": "Activado",
  "templates.disabled": "Desactivado",
  "templates.choose": "Elija…",
  "templates.typeEnter": "Escriba y pulse Intro",
  "templates.add": "Añadir",
  "templates.removeNamed": "Quitar {name}",
  "templates.inUse": "en uso: {list}",
  "templates.type.NETWORK": "Red / VLAN",
  "templates.typeDesc.NETWORK": "Segmentación VLAN, ámbito DHCP y aislamiento",
  "templates.step.NETWORK.identity": "Finalidad e identidad",
  "templates.stepDesc.NETWORK.identity": "Para qué sirve esta red",
  "templates.field.NETWORK.purpose": "Finalidad",
  "templates.option.NETWORK.purpose.production": "🏭 Producción",
  "templates.option.NETWORK.purpose.cash-registers": "🏪 Cajas registradoras",
  "templates.option.NETWORK.purpose.entrance": "🚪 Entrada / vestíbulo",
  "templates.option.NETWORK.purpose.cp": "🖥️ CP / panel de control",
  "templates.option.NETWORK.purpose.employers": "👔 Empleados / personal",
  "templates.option.NETWORK.purpose.media": "📺 Medios / AV",
  "templates.option.NETWORK.purpose.iot": "📡 Dispositivos IoT",
  "templates.option.NETWORK.purpose.guest": "🌐 Invitados / visitantes",
  "templates.option.NETWORK.purpose.vlan-only": "🔒 Solo VLAN (sin DHCP)",
  "templates.field.NETWORK.name": "Nombre de la red",
  "templates.field.NETWORK.vlanId": "VLAN ID",
  "templates.help.NETWORK.vlanId": "Sugerido a partir de las VLAN que ya usan sus plantillas",
  "templates.step.NETWORK.addressing": "Direccionamiento IP y DHCP",
  "templates.stepDesc.NETWORK.addressing": "Subred y rango de direcciones",
  "templates.field.NETWORK.subnet": "Subred",
  "templates.help.NETWORK.subnet": "p. ej., 192.168.30.0/24",
  "templates.field.NETWORK.dhcp.enabled": "Servidor DHCP",
  "templates.field.NETWORK.dhcp.start": "Inicio DHCP",
  "templates.field.NETWORK.dhcp.end": "Fin DHCP",
  "templates.field.NETWORK.dhcp.dns": "Servidores DNS",
  "templates.step.NETWORK.security": "Seguridad",
  "templates.stepDesc.NETWORK.security": "Aislamiento y protección",
  "templates.field.NETWORK.isolation": "Aislamiento de clientes",
  "templates.help.NETWORK.isolation": "Impide que los dispositivos de esta red se comuniquen entre sí",
  "templates.field.NETWORK.dhcpGuard": "Protección DHCP",
  "templates.help.NETWORK.dhcpGuard": "Bloquea servidores DHCP no autorizados",
  "templates.field.NETWORK.igmpSnooping": "IGMP snooping",
  "templates.type.WIFI": "Red wifi",
  "templates.typeDesc.WIFI": "SSID, seguridad y comportamiento de la radio",
  "templates.step.WIFI.identity": "SSID e identidad",
  "templates.stepDesc.WIFI.identity": "Nombre y visibilidad",
  "templates.field.WIFI.ssid": "SSID",
  "templates.field.WIFI.enabled": "Red activa",
  "templates.field.WIFI.isHidden": "SSID oculto",
  "templates.field.WIFI.isGuest": "Red de invitados",
  "templates.help.WIFI.isGuest": "Aísla a los clientes de la LAN",
  "templates.step.WIFI.security": "Seguridad",
  "templates.stepDesc.WIFI.security": "Cifrado y credenciales",
  "templates.field.WIFI.security": "Tipo de seguridad",
  "templates.option.WIFI.security.wpa3": "WPA3-Personal (recomendado)",
  "templates.option.WIFI.security.wpa2": "WPA2-Personal (compatible)",
  "templates.option.WIFI.security.wpa2-enterprise": "WPA2-Enterprise (RADIUS)",
  "templates.option.WIFI.security.open": "Abierta: sin contraseña",
  "templates.field.WIFI.passphrase": "Contraseña",
  "templates.help.WIFI.passphrase": "Mínimo 8 caracteres",
  "templates.field.WIFI.vlanId": "Etiqueta VLAN",
  "templates.help.WIFI.vlanId": "Vacío deja el SSID sin etiqueta",
  "templates.step.WIFI.radio": "Radio y rendimiento",
  "templates.stepDesc.WIFI.radio": "Bandas e itinerancia",
  "templates.field.WIFI.band": "Banda",
  "templates.option.WIFI.band.both": "2.4 + 5 GHz",
  "templates.option.WIFI.band.5g": "Solo 5 GHz",
  "templates.option.WIFI.band.2g": "Solo 2,4 GHz",
  "templates.option.WIFI.band.6g": "6 GHz (wifi 6E)",
  "templates.field.WIFI.txPower": "Potencia de transmisión",
  "templates.option.WIFI.txPower.auto": "Automática",
  "templates.option.WIFI.txPower.low": "Baja",
  "templates.option.WIFI.txPower.medium": "Media",
  "templates.option.WIFI.txPower.high": "Alta",
  "templates.field.WIFI.fastRoaming": "Itinerancia rápida (802.11r)",
  "templates.field.WIFI.minRssi": "Señal mínima",
  "templates.field.WIFI.scheduleEnabled": "Programación activada",
  "templates.type.WAN": "WAN / Internet",
  "templates.typeDesc.WAN": "Comportamiento de los enlaces, conmutación por error y balanceo de carga",
  "templates.step.WAN.links": "Comportamiento de los enlaces",
  "templates.stepDesc.WAN.links": "Cómo se usan los enlaces WAN",
  "templates.field.WAN.failoverEnabled": "Conmutación por error",
  "templates.help.WAN.failoverEnabled": "Pasar a un enlace de reserva cuando falla el principal",
  "templates.field.WAN.loadBalancingEnabled": "Balanceo de carga",
  "templates.help.WAN.loadBalancingEnabled": "Reparte las sesiones entre los enlaces activos",
  "templates.field.WAN.wanPortCount": "Número de enlaces WAN",
  "templates.option.WAN.wanPortCount.2": "2",
  "templates.option.WAN.wanPortCount.3": "3",
  "templates.option.WAN.wanPortCount.4": "4",
  "templates.field.WAN.healthCheckEnabled": "Comprobación de estado",
  "templates.help.WAN.healthCheckEnabled": "Prueba cada enlace y lo marca como caído si falla",
  "templates.field.WAN.priorityMode": "Modo de prioridad",
  "templates.option.WAN.priorityMode.ordered": "Orden estricto",
  "templates.option.WAN.priorityMode.weighted": "Ponderado",
  "templates.step.WAN.quality": "Calidad de servicio",
  "templates.stepDesc.WAN.quality": "Gestión de colas y MTU",
  "templates.field.WAN.smartQueueEnabled": "Smart Queue (QoS)",
  "templates.help.WAN.smartQueueEnabled": "Reduce el bufferbloat bajo carga",
  "templates.field.WAN.smartQueueAutoDetect": "Detectar la velocidad automáticamente al implantar",
  "templates.help.WAN.smartQueueAutoDetect": "Hace una prueba de velocidad al aplicar y usa el 90 % del resultado",
  "templates.field.WAN.smartQueueDown": "Velocidad de descarga",
  "templates.field.WAN.smartQueueUp": "Velocidad de subida",
  "templates.field.WAN.mtu": "MTU",
  "templates.type.CELLULAR": "Móvil / SIM",
  "templates.typeDesc.CELLULAR": "APN, política de SIM, límites de datos e itinerancia",
  "templates.step.CELLULAR.carrier": "Operador y APN",
  "templates.stepDesc.CELLULAR.carrier": "Cómo se conecta el módem a la red",
  "templates.field.CELLULAR.apn": "APN",
  "templates.help.CELLULAR.apn": "Déjelo vacío para la detección automática del operador",
  "templates.field.CELLULAR.apnUser": "Usuario del APN",
  "templates.field.CELLULAR.apnPassword": "Contraseña del APN",
  "templates.field.CELLULAR.pin": "PIN de la SIM",
  "templates.step.CELLULAR.sim": "Política de SIM",
  "templates.stepDesc.CELLULAR.sim": "Qué SIM se usa y cuándo se cambia",
  "templates.field.CELLULAR.simSwitch": "Ranura SIM preferida",
  "templates.option.CELLULAR.simSwitch.1": "SIM 1",
  "templates.option.CELLULAR.simSwitch.2": "SIM 2",
  "templates.field.CELLULAR.simFailover": "Cambio automático en caso de fallo",
  "templates.help.CELLULAR.simFailover": "Pasa a la otra SIM si se cae la conexión",
  "templates.field.CELLULAR.roaming": "Permitir itinerancia",
  "templates.help.CELLULAR.roaming": "Puede salir caro fuera de la red propia",
  "templates.step.CELLULAR.limits": "Datos y radio",
  "templates.stepDesc.CELLULAR.limits": "Límites y selección de bandas",
  "templates.field.CELLULAR.dataLimit": "Límite de datos mensual",
  "templates.help.CELLULAR.dataLimit": "Se emite una alerta al 90 % de este límite",
  "templates.field.CELLULAR.dataLimitAction": "Al alcanzar el límite",
  "templates.option.CELLULAR.dataLimitAction.alert": "Solo avisar",
  "templates.option.CELLULAR.dataLimitAction.throttle": "Limitar la velocidad",
  "templates.option.CELLULAR.dataLimitAction.disconnect": "Desconectar",
  "templates.field.CELLULAR.bandLock": "Bloqueo de banda",
  "templates.help.CELLULAR.bandLock": "Números de banda separados por comas, vacío = automático",
  "templates.field.CELLULAR.networkMode": "Modo de red",
  "templates.option.CELLULAR.networkMode.auto": "Automático",
  "templates.option.CELLULAR.networkMode.5g": "5G preferente",
  "templates.option.CELLULAR.networkMode.lte": "Solo LTE",
  "templates.option.CELLULAR.networkMode.3g": "3G de respaldo",
  "templates.type.TUNNEL": "Túnel / agregación",
  "templates.typeDesc.TUNNEL": "Agregación SpeedFusion y perfiles de cliente VPN",
  "templates.step.TUNNEL.profile": "Perfil del túnel",
  "templates.stepDesc.TUNNEL.profile": "Extremo remoto y modo de agregación",
  "templates.field.TUNNEL.name": "Nombre del perfil",
  "templates.field.TUNNEL.peerHost": "Host del extremo remoto",
  "templates.help.TUNNEL.peerHost": "Nombre de host o IP del extremo del túnel",
  "templates.field.TUNNEL.mode": "Modo",
  "templates.option.TUNNEL.mode.bonding": "Agregación: combinar ancho de banda",
  "templates.option.TUNNEL.mode.failover": "Conmutación por error: reserva activa",
  "templates.option.TUNNEL.mode.overflow": "Desbordamiento: al enlace secundario",
  "templates.field.TUNNEL.encryption": "Cifrado",
  "templates.option.TUNNEL.encryption.aes256": "AES-256",
  "templates.option.TUNNEL.encryption.aes128": "AES-128",
  "templates.option.TUNNEL.encryption.none": "Ninguno",
  "templates.step.TUNNEL.behaviour": "Comportamiento",
  "templates.stepDesc.TUNNEL.behaviour": "Reconexión y cliente VPN",
  "templates.field.TUNNEL.autoReconnect": "Reconexión automática",
  "templates.field.TUNNEL.vpnClientEnabled": "Cliente VPN",
  "templates.field.TUNNEL.vpnClientConfig": "Perfil VPN",
  "templates.type.SYSTEM": "Base del sistema",
  "templates.typeDesc.SYSTEM": "Hora, identidad y política de firmware",
  "templates.step.SYSTEM.identity": "Identidad y hora",
  "templates.stepDesc.SYSTEM.identity": "Nombres y reloj",
  "templates.field.SYSTEM.hostname": "Patrón del nombre de host",
  "templates.help.SYSTEM.hostname": "Use {site} para insertar el nombre del sitio",
  "templates.field.SYSTEM.timezone": "Zona horaria",
  "templates.field.SYSTEM.ntp": "Servidor NTP",
  "templates.step.SYSTEM.maintenance": "Mantenimiento",
  "templates.stepDesc.SYSTEM.maintenance": "Firmware y copias de seguridad",
  "templates.field.SYSTEM.firmwareCheck": "Comprobar el firmware al aplicar",
  "templates.field.SYSTEM.firmwareUpgrade": "Instalar actualizaciones automáticamente",
  "templates.help.SYSTEM.firmwareUpgrade": "Aplica la última versión: provoca un reinicio",
  "templates.field.SYSTEM.backup": "Hacer copia de seguridad antes de los cambios",
  "templates.type.LINK": "Enlace inalámbrico",
  "templates.typeDesc.LINK": "Ajustes de radio punto a punto o punto a multipunto: una plantilla por extremo",
  "templates.step.LINK.role": "Función",
  "templates.stepDesc.LINK.role": "Qué extremo del enlace es este dispositivo",
  "templates.field.LINK.topology": "Topología",
  "templates.option.LINK.topology.ptp": "Punto a punto: una estación",
  "templates.option.LINK.topology.ptmp": "Punto a multipunto: varias estaciones",
  "templates.field.LINK.role": "Este extremo",
  "templates.help.LINK.role": "Aplique primero la plantilla de la estación y después, en 10 minutos, la del punto de acceso; al revés, las estaciones pierden el enlace. Una estación conserva sus nuevos ajustes cuando el punto de acceso coincide; si no ocurre a tiempo, vuelve atrás por sí sola",
  "templates.option.LINK.role.ap": "Punto de acceso (el sector / el extremo cercano)",
  "templates.option.LINK.role.station": "Estación (el CPE / el extremo lejano)",
  "templates.field.LINK.interface": "Interfaz de radio",
  "templates.help.LINK.interface": "Vacío elige la radio del enlace si la hay (una estación, una radio de 60 GHz o NV2/Nstreme); un dispositivo con varias radios necesita el nombre (wlan60-1, wlan1, wifi1 …)",
  "templates.step.LINK.radio": "Radio",
  "templates.stepDesc.LINK.radio": "Ambos extremos deben coincidir",
  "templates.field.LINK.ssid": "Nombre del enlace (SSID)",
  "templates.field.LINK.linkKey": "Clave del enlace",
  "templates.help.LINK.linkKey": "Al menos 8 caracteres. Clave NV2, frase de contraseña WPA2/WPA3 o contraseña de 60 GHz, según la radio",
  "templates.field.LINK.frequency": "Frecuencia",
  "templates.help.LINK.frequency": "5 GHz p. ej. 5500. 60 GHz: 58320, 60480, 62640, 64800 o 66000. Vacío mantiene la actual",
  "templates.field.LINK.channelWidth": "Ancho de canal",
  "templates.help.LINK.channelWidth": "No se usa en 60 GHz",
  "templates.option.LINK.channelWidth.20": "20 MHz",
  "templates.option.LINK.channelWidth.40": "40 MHz",
  "templates.option.LINK.channelWidth.80": "80 MHz",
  "templates.field.LINK.protocol": "Protocolo",
  "templates.help.LINK.protocol": "Solo el controlador «wireless» de 2,4/5 GHz; las radios wifi 6 usan siempre 802.11",
  "templates.option.LINK.protocol.nv2": "NV2 (TDMA de MikroTik: lo mejor para enlaces exteriores)",
  "templates.option.LINK.protocol.802.11": "802.11 (wifi estándar)",
  "templates.option.LINK.protocol.nstreme": "Nstreme",
  "templates.option.LINK.protocol.any": "Cualquiera (seguir al punto de acceso)",
  "templates.field.LINK.txPower": "Potencia de transmisión",
  "templates.help.LINK.txPower": "Vacío mantiene el ajuste actual. No supere el límite de su dominio regulatorio",
  "templates.type.CYBERSECURE": "CyberSecure",
  "templates.typeDesc.CYBERSECURE": "IPS, filtrado de contenido y gestión de amenazas",
  "templates.step.CYBERSECURE.ips": "Prevención de intrusiones",
  "templates.stepDesc.CYBERSECURE.ips": "Bloqueo basado en firmas",
  "templates.field.CYBERSECURE.ips.enabled": "Activar IPS/IDS",
  "templates.field.CYBERSECURE.ips.mode": "Modo",
  "templates.option.CYBERSECURE.ips.mode.ips": "IPS: bloquear amenazas",
  "templates.option.CYBERSECURE.ips.mode.ids": "IDS: solo avisar",
  "templates.step.CYBERSECURE.content": "Filtrado de contenido",
  "templates.stepDesc.CYBERSECURE.content": "Bloqueo por categoría y dominio",
  "templates.field.CYBERSECURE.contentFilter.enabled": "Activar el filtrado de contenido",
  "templates.field.CYBERSECURE.contentFilter.categories": "Categorías bloqueadas",
  "templates.option.CYBERSECURE.contentFilter.categories.adult": "🔞 Adultos",
  "templates.option.CYBERSECURE.contentFilter.categories.gambling": "🎰 Apuestas",
  "templates.option.CYBERSECURE.contentFilter.categories.malware": "🦠 Malware",
  "templates.option.CYBERSECURE.contentFilter.categories.p2p": "⬇️ P2P",
  "templates.option.CYBERSECURE.contentFilter.categories.ads": "📢 Anuncios",
  "templates.option.CYBERSECURE.contentFilter.categories.social": "💬 Redes sociales",
  "templates.option.CYBERSECURE.contentFilter.categories.streaming": "📺 Streaming",
  "templates.option.CYBERSECURE.contentFilter.categories.games": "🎮 Juegos",
  "templates.field.CYBERSECURE.contentFilter.customBlockedUrls": "Dominios bloqueados propios",
  "templates.help.CYBERSECURE.contentFilter.customBlockedUrls": "Añada los dominios de uno en uno",
  "templates.type.COMPANY_DEFAULTS": "Valores predeterminados de la empresa",
  "templates.typeDesc.COMPANY_DEFAULTS": "Un conjunto de plantillas que se aplica a cada sitio nuevo",
  "templates.step.COMPANY_DEFAULTS.bundle": "Plantillas incluidas",
  "templates.stepDesc.COMPANY_DEFAULTS.bundle": "Se aplican en orden cuando un sitio se conecta",
  "templates.field.COMPANY_DEFAULTS.networkTemplateIds": "Plantillas de red",
  "templates.field.COMPANY_DEFAULTS.wifiTemplateIds": "Plantillas wifi",
  "templates.field.COMPANY_DEFAULTS.wanTemplateId": "Plantilla WAN",
  "templates.field.COMPANY_DEFAULTS.cellularTemplateId": "Plantilla móvil",
  "templates.field.COMPANY_DEFAULTS.tunnelTemplateId": "Plantilla de túnel",
  "templates.field.COMPANY_DEFAULTS.systemTemplateId": "Plantilla del sistema",
}

export default templates
